import React from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ZERO_ICONS } from '../../constants/zeroIcons';
import { useNow } from '../../hooks/useOperationsData';
import { formatClock, formatRelative } from '../../utils/numberFormat';

export interface FreshnessLabelProps {
  /** When this browser received the data (ms epoch). Never a server-side "live" claim. */
  fetchedAt: number | null;
  refreshing?: boolean;
  onRefresh?: () => void;
}

/** "Updated 2 min ago" + refresh — the honest freshness of fetched data. */
const FreshnessLabel: React.FC<FreshnessLabelProps> = ({ fetchedAt, refreshing = false, onRefresh }) => {
  const { t, i18n } = useTranslation();
  const now = useNow();
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', minWidth: 0 }}>
      {fetchedAt ? (
        <Tooltip title={t('freshness.fetchedAt', { time: formatClock(fetchedAt, i18n.language) })}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <ZERO_ICONS.freshness aria-hidden sx={{ fontSize: 16 }} />
            <Typography variant="caption" component="span" noWrap sx={{ fontWeight: 500 }}>
              {t('freshness.updated', { time: formatRelative(fetchedAt, Math.max(now, fetchedAt), i18n.language) })}
            </Typography>
          </Box>
        </Tooltip>
      ) : null}
      {onRefresh ? (
        <Tooltip title={t('common.refresh')}>
          <span>
            <IconButton size="small" onClick={onRefresh} disabled={refreshing} aria-label={t('common.refresh')} sx={{ color: 'text.secondary' }}>
              {refreshing ? <CircularProgress size={16} thickness={5} color="inherit" /> : <ZERO_ICONS.refresh sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export default FreshnessLabel;
